import { useEffect, useSyncExternalStore } from "react";
import { createPortal } from "react-dom";
import { realtimeClient } from "@/lib/realtimeClient";
import { dismissConnectivityToasts } from "@/lib/toast";
import { Spinner } from "@/components/ui/Spinner";
import {
  getServerUpdateServerSnapshot,
  getServerUpdateSnapshot,
  markServerUpdateActive,
  startServerUpdateWatch,
  subscribeServerUpdate,
} from "@/features/serverUpdate/serverUpdateStore";

/**
 * Leiste über der gesamten App, solange ein Live-Deploy die API neu startet.
 * Bleibt sichtbar, auch wenn einzelne Seiten gerade „Server nicht erreichbar“ sehen würden.
 */
export function ServerUpdateBanner() {
  const { active, message } = useSyncExternalStore(
    subscribeServerUpdate,
    getServerUpdateSnapshot,
    getServerUpdateServerSnapshot,
  );

  useEffect(() => startServerUpdateWatch(), []);

  useEffect(() => {
    const stop = realtimeClient.addListener({
      events: ["server_maintenance"],
      fallbackIntervalMs: 24 * 60 * 60 * 1000,
      onChange: (payload) => {
        if (payload?.event !== "server_maintenance") return;
        const data = payload.data as { active?: boolean; message?: string } | undefined;
        if (data?.active === true) markServerUpdateActive(data.message);
      },
    });
    return stop;
  }, []);

  useEffect(() => {
    if (active) dismissConnectivityToasts();
  }, [active]);

  if (!active || typeof document === "undefined") return null;

  return createPortal(
    <div
      role="status"
      aria-live="polite"
      className="fixed inset-x-0 top-0 z-[2100] border-b border-amber-900/30 bg-amber-600 px-4 py-2.5 text-white shadow-md pt-[max(0.625rem,env(safe-area-inset-top))]"
    >
      <div className="mx-auto flex max-w-3xl items-center justify-center gap-2.5 text-center text-sm font-semibold">
        <Spinner className="h-4 w-4 shrink-0 text-white" />
        <p>{message}</p>
      </div>
    </div>,
    document.body,
  );
}
